import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportReport } from 'api/reports';
import { useFetchQueryParams } from 'assets/js/pages/reports/useFetchQueryParams';
import * as Dialog from 'components/dialog';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { GivebutterAxiosError } from 'types/error';

interface ExportReportDialogProps extends Dialog.DialogProps {}

const ExportReportDialog = (props: ExportReportDialogProps) => {
    const { children, ...rest } = props;
    const { accountId } = useCurrentAccount();
    const { email } = useCurrentUser();
    const { data: report } = useCurrentReport();
    const { query } = useContext(TableCoreStateContext);
    const params = useFetchQueryParams();
    const { error, success } = useToast();
    const rowCount = query?.data.meta.total ?? 0;

    const { isPending, mutate: submit } = useMutation({
        mutationFn: () =>
            exportReport(accountId, report.id, {
                query: {
                    columns: params?.columns,
                    display_settings: params?.display_settings,
                    filters: params?.filters,
                    graph_settings: params?.graph_settings,
                    date_range: params?.date_range,
                    timestamp_column: report.query.timestamp_column,
                },
            }),
        onSuccess: () => {
            success('Successfully exported report!');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unable to export report');
        },
    });

    return (
        <Dialog.Root {...rest}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <div className={'flex flex-col gap-6'}>
                        <Avatar.Root>
                            <Avatar.Fallback>
                                <FontAwesomeIcon icon={faDownload} />
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <div className={'flex flex-col gap-2'}>
                            <p className={'text-xl font-medium'}>Export report</p>
                            <p className={'text-sm text-gray-600'}>
                                Any additional filters or unsaved changes will be applied to your
                                CSV file. The report may take a few minutes to generate and will be
                                sent to <span className={'font-medium'}>{email}</span> once it’s
                                ready.
                            </p>
                        </div>
                        <div className={'flex flex-col gap-2'}>
                            <p className={'text-sm font-medium'}>In export</p>
                            <p className={'flex gap-1 text-gray-900'}>
                                <span className={'font-medium'}>{report.name}</span>(
                                {`${rowCount} ${pluralize(rowCount, 'row')}`})
                            </p>
                        </div>
                        <div className={'mt-4 flex w-full flex-wrap-reverse items-center gap-3'}>
                            <div className={'flex-1'}>
                                <Dialog.Cancel asChild={true}>
                                    <Button
                                        className={'w-full'}
                                        color={'neutral'}
                                        variant={'outline'}
                                    >
                                        Cancel
                                    </Button>
                                </Dialog.Cancel>
                            </div>
                            <div className={'flex-1'}>
                                <Button
                                    variant="solid"
                                    disabled={isPending}
                                    className={'w-full'}
                                    onClick={() => submit()}
                                >
                                    Export
                                </Button>
                            </div>
                        </div>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ExportReportDialogTrigger = Dialog.Trigger;

const Root = ExportReportDialog;
const Trigger = ExportReportDialogTrigger;

export {
    ExportReportDialog,
    ExportReportDialogTrigger,
    //
    Root,
    Trigger,
};
